import React, { useContext, useEffect, useState } from 'react';
import { AccessControl, getTranslatedText } from '@nile/components';
import {
  WAILS_EVENT,
  closeChatWindow,
  killChatWindow,
  postMessageToWails
} from '@nile/components/src/Pages/SystemTrayChatPage/constants';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import ChatTitle from '@nile/components/src/Pages/SystemTrayChatPage/ChatTitle';
import CloseChat from '@nile/components/src/Pages/SystemTrayChatPage/subpages/CloseChat';
import ChatActionCell from '@nile/components/src/Pages/SystemTrayChatPage/subpages/ChatActionCell';
import { FormattedMessage, useIntl } from 'react-intl';
import { DisplayText, TextStyle, Tippy } from '@superops/ui-design-system';
import {
  AccountClient,
  GET_INTEGRATIONS_BY_IDS,
  useLazyQuery
} from '@nile/graphql';
import { INTEGRATIONS_NON_SOLO } from '@nile/rmm/src/modules/asset-management/constants/asset-list';
import { fetchAssetList } from './helper';

function ChatHeader(props) {
  const {
    isWeb,
    setMinimise,
    minimise,
    roundedBorder,
    data = {},
    ticketData,
    setTimerPopupData = null,
    threadData = {},
    sendMessage,
    chatStatus,
    setChatStatus,
    closeSession,
    threadLoading,
    messageCount,
    clearChatData,
    isChooseTemplate,
    isOnDemandChatVersion
  } = props;
  const intl = useIntl();
  const tabContext = useContext(TabContext);

  const [configuredIntegrations, setConfiguredIntegrations] = useState([]);
  const [assetData, setAssetData] = useState([]);
  const [integrationLoading, setIntegrationLoading] = useState(true);

  const [fetchIntegrationsByIds] = useLazyQuery(GET_INTEGRATIONS_BY_IDS, {
    client: AccountClient,
    fetchPolicy: 'network-only',

    onCompleted: (res) => {
      if (res?.getIntegrationsByIds) {
        const activatedIntegrations = res?.getIntegrationsByIds.filter(
          (integration) => {
            return integration.activated === true;
          }
        );
        setConfiguredIntegrations(activatedIntegrations);
        setIntegrationLoading(false);
      }
    }
  });

  const getActions = () => {
    if (!isWeb) {
      if (chatStatus?.sessionConnected) {
        return [
          {
            id: 'minimize',
            iconName: 'SquareMinus',
            iconColor: 'white',
            onClick: () =>
              postMessageToWails({ eventName: WAILS_EVENT.MINIZE_WINDOW })
          }
        ];
      }
      if (chatStatus?.sessionClosed) {
        return [
          {
            id: 'closeIcon',
            iconName: 'Close',
            iconColor: 'white',
            onClick: () => {
              isOnDemandChatVersion ? killChatWindow() : closeChatWindow();
              clearChatData();
            }
          }
        ];
      }
      return;
    }
    if (isWeb && !threadData?.chatActiveSession) {
      return [
        {
          id: 'minimize',
          iconName: 'SquareMinus',
          iconColor: 'white',
          onClick: () => {
            setMinimise((prevState) => !prevState);
          }
        }
      ];
    }
    if (isChooseTemplate && isWeb && !threadLoading) {
      return [
        {
          id: 'settingsIcon',
          iconName: 'SettingsDark',
          iconColor: 'black',
          onClick: () =>
            tabContext.openTab({
              module: 'ONBOARD',
              url: '/onboard/templates',
              state: {
                section: 'agent-chat-templates'
              }
            })
        },
        {
          id: 'closeIcon',
          iconName: 'Close',
          iconColor: 'black',
          onClick: () => {
            closeSession({
              convThread: threadData
            });
          }
        }
      ];
    }
    if (chatStatus?.sessionClosed || !chatStatus?.sessionConnected) {
      return [
        {
          id: 'minimize',
          iconName: 'SquareMinus',
          iconColor: 'white',
          onClick: () => {
            setMinimise((prevState) => !prevState);
          }
        },
        {
          id: 'closeIcon',
          iconName: 'Close',
          iconColor: 'white',
          onClick: () => {
            closeSession({
              convThread: threadData
            });
          }
        }
      ];
    }
    const menus = [
      {
        id: 'minimize',
        iconName: 'SquareMinus',
        iconColor: 'white',
        onClick: () => {
          setMinimise((prevState) => !prevState);
        }
      },
      {
        id: 'remoteDesktop',
        component: (
          <AccessControl
            roleToCheck="remoteDesktop.allow"
            render={
              <Tippy
                title={getTranslatedText(
                  intl,
                  'msp.chat.action.button.tooltip'
                )}
              >
                {integrationLoading ? (
                  <></>
                ) : (
                  <ChatActionCell
                    cell={{
                      row: {
                        original: {
                          ...assetData,
                          ...assetData?.workstationInfo
                        }
                      },
                      column: {
                        configuredIntegrations: configuredIntegrations
                      }
                    }}
                    originalData={data}
                    sendMessage={sendMessage}
                  />
                )}
              </Tippy>
            }
          />
        )
      },
      {
        id: 'closechat',
        component: (
          <CloseChat
            data={data}
            ticketData={ticketData}
            setTimerPopupData={setTimerPopupData}
            threadData={threadData}
            closeSession={closeSession}
          />
        )
      }
    ];
    return menus;
  };

  const handleHeaderClick = () => {
    if (minimise && isWeb) setMinimise((prevState) => !prevState);
  };

  const openTicket = () => {
    if (isWeb) {
      tabContext.openTab({
        module: 'TICKET',
        data: {
          ...threadData?.workItem,
          ticketId: threadData?.workItem?.workId
        }
      });
    }
  };

  const openAsset = () => {
    if (isWeb) {
      tabContext.openTab({
        module: 'ASSET_DETAILS',
        data: { ...threadData?.asset, id: threadData?.asset?.assetId }
      });
    }
  };

  useEffect(() => {
    if (isWeb) {
      const assetid = data?.assetInfo?.assetId || data?.asset?.assetId;
      fetchAssetList(assetid).then((dta) =>
        setAssetData(dta?.data?.getAllAssets?.assets?.[0])
      );
      fetchIntegrationsByIds({
        variables: {
          integrationIds: INTEGRATIONS_NON_SOLO
        }
      });
    }
  }, []);

  const name = isWeb
    ? data?.loggedinUser?.userName ||
      threadData?.recipients?.receiver?.[0]?.name
    : threadData?.recipients?.initiator?.user?.name;

  const desc = isWeb ? (
    <div className="vividBlue pt-2">
      <DisplayText weight="Medium">
        <span>
          <span className="ptr" onClick={openTicket}>
            {' '}
            {`#${threadData?.workItem?.displayId ||
              getTranslatedText(intl, 'attribute.ticketId')}`}
          </span>{' '}
          -{' '}
          <span className="ptr" onClick={openAsset}>
            {threadData?.asset?.name}
          </span>
        </span>
      </DisplayText>
    </div>
  ) : (
    <div className="text-white g10 pt-2">
      <span className="flex g5">
        <span className="ptr">
          {`#${threadData?.workItem?.displayId ||
            getTranslatedText(intl, 'attribute.ticketId')}`}
        </span>
        -
        <span className="ptr">
          <FormattedMessage id="ticket.details.chat.from" />
        </span>
        <span className="ptr">
          <TextStyle weight="SemiBold">
            {threadData?.workItem?.client?.name || 'client'}
          </TextStyle>
        </span>
      </span>
    </div>
  );

  return (
    <ChatTitle
      roundedBorder={roundedBorder}
      collapse={{
        state: minimise,
        action: setMinimise
      }}
      profile={{
        name: name,
        description: desc
      }}
      messageCount={messageCount}
      actions={getActions()}
      chatStatus={chatStatus}
      setChatStatus={setChatStatus}
      closeSession={closeSession}
      loading={threadLoading}
      onHeaderClick={handleHeaderClick}
      isChooseTemplate={isChooseTemplate}
      isWeb={isWeb}
    />
  );
}

export default ChatHeader;
